/**
 * <s-count-up>: counts the first number found in its first child element up from
 * zero when it scrolls into view, keeping any prefix/suffix ("50,000+", "4.97★").
 * The markup always holds the final value, so no-JS, reduced-motion and the
 * theme editor show the real figure.
 */
class SCountUp extends HTMLElement {
  #observer = null;
  #frame = 0;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.Shopify?.designMode) return;
    if (!('IntersectionObserver' in window)) return;

    const target = this.firstElementChild;
    if (!target) return;
    const text = target.textContent.trim();
    const match = text.match(/\d[\d,.]*/);
    if (!match) return;

    const raw = match[0].replace(/[.,]$/, '');
    const decimals = (raw.split('.')[1] || '').length;
    const value = parseFloat(raw.replace(/,/g, ''));
    if (!Number.isFinite(value)) return;

    const before = text.slice(0, match.index);
    const after = text.slice(match.index + raw.length);
    const format = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: raw.includes(','),
    });

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        const start = performance.now();
        const duration = 1600;
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - progress, 4);
          target.textContent = before + format.format(value * eased) + after;
          if (progress < 1) this.#frame = requestAnimationFrame(tick);
        };
        this.#frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('s-count-up')) customElements.define('s-count-up', SCountUp);
